import { useRef, useState } from 'react'

import { GAMES, SUDOKU_SIZES, TEDDIES, type SudokuSize } from '@hazel/shared'

import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { KeyCap } from '../components/KeyCap.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { Announce } from './Announce.js'
import { LeaveGame } from './LeaveGame.js'
import { Milestone } from './Milestone.js'
import { PlayAgain } from './PlayAgain.js'
import { RaceTrack } from './RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from './raceArt.js'
import { SudokuCell } from './SudokuCell.js'
import { boxEdges, columnsFor, stepFrom, SUDOKU_LAYOUT } from './sudokuLayout.js'
import { useCueOnChange, useCueOnEach, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useSound } from '../sound/sound-context.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { useRound, type DealtRound } from './useRound.js'
import { useRoundFocus } from './useRoundFocus.js'
import { useSudoku, type SudokuConfigFromRound } from './useSudoku.js'

/**
 * What each grid is called, and what it is played with.
 *
 * The second line is the point. At 4×4 and 6×6 the squares hold her teddies; at
 * 9×9 they hold numbers, because nine photographs at 43px on a phone do not
 * stay apart and nine digits do. She should know that before she chooses rather
 * than after, so the choice says so.
 */
const SIZE_LABELS: Readonly<Record<SudokuSize, { name: string; with: string }>> = {
  4: { name: 'Small', with: 'Four teddies' },
  6: { name: 'Middling', with: 'Six teddies' },
  9: { name: 'The proper one', with: 'Numbers, 1 to 9' },
}

/**
 * How big a grid, asked before there is one.
 *
 * She picks, exactly as she picks a board in Sky's Memory Match, rather than
 * the size being unlocked by her level. She can already do a 9×9 with numbers,
 * and a ladder that started her on 4×4 would make her climb to what she can do
 * already. Her level within each size still decides how much is filled in for
 * her and how hard the reasoning is.
 */
function ChooseSize({ onChoose }: { onChoose: (size: SudokuSize) => void }) {
  const leaveOnEscape = useLeaveOnEscape()

  useGameKeys((event) => {
    // The prompt is a screen of its own, so it needs the same way out as every
    // other one. Getting stuck on the way in would be a worse trap than getting
    // stuck on the way out.
    if (event.key === 'Escape') return void leaveOnEscape(false)

    const digit = digitFrom(event)
    if (!digit) return

    // Numbered the way the picker's games are, so the same habit works twice.
    const chosen = SUDOKU_SIZES[Number(digit) - 1]
    if (chosen !== undefined) onChoose(chosen)
  })

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-5 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:gap-3">
      <Card className="flex w-full flex-col items-center gap-4">
        <h2 className="text-(length:--text-heading)">How big a grid?</h2>

        <div className="flex w-full flex-col gap-2 [[data-scale=tv]_&]:gap-1">
          {SUDOKU_SIZES.map((option, index) => (
            <Button
              key={option}
              variant="secondary"
              block
              onClick={() => onChoose(option)}
              aria-label={`${SIZE_LABELS[option].name}, ${option} by ${option}, ${SIZE_LABELS[option].with}. Press ${index + 1}`}
            >
              <span className="flex w-full items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                    <KeyCap>{index + 1}</KeyCap>
                  </span>
                  {SIZE_LABELS[option].name}
                </span>
                <span className="text-ink-muted text-(length:--text-small)">
                  {`${option} × ${option} · ${SIZE_LABELS[option].with}`}
                </span>
              </span>
            </Button>
          ))}
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={[
          { keys: ['1', '–', String(SUDOKU_SIZES.length)], does: 'Pick a grid' },
          LEAVE_SHORTCUT,
        ]}
      />
    </div>
  )
}

export function Sudoku() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.sudoku.title)

  /*
    The screen's music, not the round's. Asked here so it also survives another
    go, which is the same screen and should not restart the track.
  */
  useRoundMusic(TRACK_FOR_GAME.sudoku)

  // Nothing is dealt until she has said how big, so the size is settled before
  // a round exists rather than being changed underneath one.
  const [size, setSize] = useState<SudokuSize | null>(null)

  if (size === null) return <ChooseSize onChoose={setSize} />

  return <SudokuRound size={size} />
}

function SudokuRound({ size }: { size: SudokuSize }) {
  const { round, again, failed } = useRound<SudokuConfigFromRound>({ game: 'sudoku', size })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a game just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Drawing the grid…
      </p>
    )
  }

  // Keyed on the seed, so a new round deals a new grid rather than leaving the
  // old squares on screen under a new heading.
  return <SudokuGrid key={round.seed} round={round} size={size} onAgain={again} />
}

function SudokuGrid({
  round,
  size,
  onAgain,
}: {
  round: DealtRound<SudokuConfigFromRound>
  size: SudokuSize
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useSudoku(round, onAgain)
  const sound = useSound()
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  /**
   * Where the keyboard is pointing.
   *
   * A grid needs a cursor when there is no mouse, and this one needs it most:
   * tabbing to the square you want out of eighty-one is not a game.
   */
  const [cursor, setCursor] = useState(0)

  /*
    The cursor and the clash are watched; putting a teddy in and taking one out
    are taken at the gesture.

    That split is Small Rabbit's lesson rather than a preference. Her tiles
    cued from a derived count, and the count collapsed whenever the letters
    changed underneath it — so every solved word was followed by a phantom
    "tile going back". The same trap is here: `changes` counts rubbing one
    square out *and* rubbing the whole grid out, so one derived count cannot
    tell those two apart and the second would have been announced as the first.

    The cursor is this component's own state and the clash is a fact about the
    grid, so neither can collapse under anything.
  */
  useCueOnChange(cursor, 'move')
  useCueOnEach(view.conflicts, 'clash')
  useRoundEndCues(view.status !== 'playing', game.milestone)

  const columns = columnsFor(size)
  const teddyFor = (value: number | null) =>
    value === null ? null : (view.teddies[value - 1] ?? null)

  /** The square buttons, so the cursor can carry real focus with it. */
  const cells = useRef(new Map<number, HTMLButtonElement>())

  function moveTo(next: number | null): void {
    if (next === null) return

    setCursor(next)

    // Focus goes where the ring goes, every time. Without this the ring and the
    // browser's own focus are two separate things, and a keypress lands on a
    // square nobody was pointing at.
    const target = cells.current.get(next)
    target?.focus()
    if (document.activeElement !== target) board.current?.focus()
  }

  function fill(value: number | null): void {
    const square = view.cells[cursor]
    if (!square || square.given) return
    // Pressing the same key twice changes nothing, and should not sound as
    // though it did.
    if (square.value === value) return

    sound.play(value === null ? 'tile.back' : 'tile.take')
    game.place(cursor, value)
  }

  useGameKeys((event) => {
    // Before the status gate: a finished grid is exactly when somebody wants
    // out, and there is nothing to lose at any point.
    if (event.key === 'Escape') return void leaveOnEscape(false)

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        return moveTo(stepFrom(size, cursor, 'left'))
      case 'ArrowRight':
        return moveTo(stepFrom(size, cursor, 'right'))
      case 'ArrowUp':
        return moveTo(stepFrom(size, cursor, 'up'))
      case 'ArrowDown':
        return moveTo(stepFrom(size, cursor, 'down'))
      case 'Backspace':
      case 'Delete':
        event.preventDefault()
        return fill(null)
      default:
    }

    // Rubbing the whole lot out. Upper and lower case, because it is one key to
    // her and Caps Lock is not a state anybody checks.
    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault()
      sound.play('grid.clear')
      return game.clear()
    }

    const digit = digitFrom(event)
    if (!digit) return

    const value = Number(digit)
    // A 7 on a 4×4 is not a square anybody can fill. Ignored rather than
    // refused, so nothing flashes at her for pressing a key that does nothing.
    if (value < 1 || value > size) return

    event.preventDefault()
    fill(value)
  })

  const layout = SUDOKU_LAYOUT[size]

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1">
      {/*
        On the television the grid sits on the left and everything else beside
        it, which is the whole of why a 9\u00d79 is legible there at all.

        A sudoku is square and a television is 16:9, so a grid bounded by the
        height it is given leaves about fourteen hundred pixels of width doing
        nothing \u2014 while the tally, the race and the teddy key stack up *above*
        it and take the height it needed. Measured before this: 361px of grid
        on a 6\u00d76, which is a 40px square on a 9\u00d79, with a third of the screen
        empty on either side of it.

        `contents` off the television, so a phone keeps the single column it
        wants and none of this applies to it.
      */}
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:flex-row [[data-scale=tv]_&]:items-stretch [[data-scale=tv]_&]:gap-6"
      >
        <div className="contents [[data-scale=tv]_&]:order-2 [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:w-80 [[data-scale=tv]_&]:flex-none [[data-scale=tv]_&]:flex-col [[data-scale=tv]_&]:justify-center [[data-scale=tv]_&]:gap-6">
          {/*
            Squares filled, not squares right. Announcing correctness would be
            announcing the answer, and the live region is held to exactly the
            same rule the colours are.
          */}
          <Announce>
            {[
              view.status === 'playing'
                ? `${view.filled} of ${view.toFill} squares filled${
                    view.conflicts > 0 ? `, ${view.conflicts} clashing` : ''
                  }`
                : `You got it. The whole grid, ${view.toFill} squares.`,
              game.milestone ? `That\u2019s ${game.milestone} games played.` : '',
            ]
              .filter(Boolean)
              .join(' ')}
          </Announce>

          {view.status === 'playing' ? (
            <>
              <p className="text-ink-muted text-(length:--text-small)">
                {`${view.filled} of ${view.toFill} squares filled`}
                {view.conflicts > 0 ? ` \u00b7 ${view.conflicts} clashing` : ''}
              </p>

              {/*
                Stripey, walking to an apple.

                One step per square filled, and nobody racing her — the same
                reasoning as Sky swimming to her pearl. A puzzle is not a race,
                and inventing an opponent would invent jeopardy that is not
                there.

                Filled, and deliberately never *right*: a racer that only moved
                on correct squares would tell her which of them were correct,
                which is the answer leaking out through the scenery.
              */}
              <RaceTrack
                goal={RACE_ART.apple}
                racers={[
                  {
                    key: 'stripey',
                    name: 'Stripey',
                    picture: racerPicture('stripey'),
                    steps: view.filled,
                    of: view.toFill,
                    tint: LANE.stripey,
                  },
                ]}
              />

              {/*
                The chooser, and on a phone the only way to play at all.

                On the television it is documentation — the keycaps say which
                digit is which teddy — and it stays hidden on the grid of
                numbers there, where a digit is itself. Everywhere else it is
                the control, at every size, because a finger has nothing else.
              */}
              <div className={view.teddies.length > 0 ? '' : 'contents [[data-scale=tv]_&]:hidden'}>
                <Choices
                  size={size}
                  teddies={view.teddies}
                  remaining={view.remaining}
                  disabled={view.cells[cursor]?.given ?? false}
                  onChoose={fill}
                />
              </div>

              {/*
                Backspace and R, for a room with no keyboard. Hidden on the
                television, which has both keys and a legend that names them.
              */}
              <div className="flex flex-wrap justify-center gap-2 [[data-scale=tv]_&]:hidden">
                <Button
                  variant="secondary"
                  onClick={() => fill(null)}
                  disabled={view.cells[cursor]?.given ?? false}
                >
                  Rub one out
                </Button>
                <Button variant="secondary" onClick={game.clear}>
                  Start again
                </Button>
              </div>
            </>
          ) : (
            /*
              When it is finished, the finish has this column to itself.

              The tally, the race and the teddy key all still had something to
              say a moment ago and now say it worse than the sentence below
              them does — the race is at the apple, the tally reads "36 of 36",
              and there is nothing left to put in. Kept on screen they were not
              merely redundant: on a 6×6 the key is six teddies tall in a 320px
              column, and the column ran past the bottom of the television and
              took "Play again" with it. Measured at 1008px on a 1080 screen,
              which on a set with no mouse is a button that does not exist.
            */
            <div className="flex flex-col items-center gap-3">
              <p className="font-display text-(length:--text-title)">You got it!</p>
              <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                {view.changes === 0
                  ? 'And never changed your mind once.'
                  : `${view.changes} ${view.changes === 1 ? 'change' : 'changes'} of mind along the way.`}
              </p>
              {game.milestone ? <Milestone games={game.milestone} /> : null}

              <PlayAgain label="Play again" does="to play again" onAgain={game.again} />
            </div>
          )}
        </div>

        <div className="contents [[data-scale=tv]_&]:order-1 [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:min-w-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:flex-col [[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:justify-center">
          <div
            /*
              `group`, not `grid`, however grid-shaped it looks.

              An ARIA grid promises rows and gridcells, and there are none: the
              board is one flat CSS grid of buttons, so a screen reader told it
              had entered a grid would offer grid navigation and report
              positions that are not in the tree. Nothing is lost by not
              claiming it — every square already says "Row 2, column 3, Rudolf"
              for itself, and the arrow keys are the game's own.

              `group` rather than no role at all, because `aria-label` on a bare
              `div` is ignored and the board would lose its name along with its
              false structure.
            */
            role="group"
            aria-label={`${size} by ${size} grid`}
            /*
              `flex-1`, not `h-full`.

              `h-full` is a percentage of a parent whose own height comes from
              its content, so it resolves to auto and the grid sized itself
              from its squares instead. Growing into the box is the flex item's
              job, and the aspect ratio then makes the width follow the height
              \u2014 the same arrangement Sky's board uses.
            */
            className={`border-ink grid w-full max-w-xl border-2 ${layout.className} [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:w-auto [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:flex-1 ${layout.tvClassName}`}
          >
            {view.cells.map((cell) => (
              <SudokuCell
                key={cell.cell}
                cell={cell.cell}
                columns={columns}
                value={cell.value}
                teddy={teddyFor(cell.value)}
                given={cell.given}
                conflicting={cell.conflicting}
                cursor={cell.cell === cursor}
                edges={boxEdges(size, cell.cell)}
                valueClassName={layout.valueClassName}
                disabled={cell.given || view.status !== 'playing'}
                ref={(element) => {
                  if (element) cells.current.set(cell.cell, element)
                  else cells.current.delete(cell.cell)
                }}
                // The cursor follows the finger, and takes real focus with it,
                // for the same reason Sky's cards do: two things pointing at
                // different squares is how the next arrow key steps from
                // somewhere nobody touched.
                onPick={(element) => {
                  element?.focus()
                  setCursor(cell.cell)
                }}
              />
            ))}
          </div>
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={
          view.status === 'playing'
            ? [
                { keys: ['←', '↑', '↓', '→'], does: 'Move around the grid' },
                { keys: ['1', '–', String(size)], does: 'Put one in' },
                { keys: ['Backspace'], does: 'Rub one out' },
                { keys: ['R'], does: 'Start the grid again' },
                LEAVE_SHORTCUT,
              ]
            : [{ keys: ['Enter'], does: 'Play again' }, LEAVE_SHORTCUT]
        }
      />
    </div>
  )
}

/**
 * What she can put in a square, and how many of each are left.
 *
 * **Buttons, because on a phone this is the only way to play at all.** It was
 * a list of `<li>` — written as documentation for a television, where a
 * Bluetooth keyboard does the typing and this only had to say which digit was
 * which teddy. On a phone that left a grid she could select squares in and
 * never fill: nothing to tap, and at 9×9 no strip on screen at any price.
 *
 * So it is the same strip and it is now the control. The keycap stays for the
 * room that has a keyboard; the finger gets something to press. And the grid
 * of numbers has one too, which it never did.
 *
 * **A spent value is dimmed and still works.** When every one of a teddy is on
 * the grid there are none left to place, and saying so saves her counting. It
 * does not *refuse* — this app's rule is that she may be wrong on the way to
 * being right, and a clashing square is shown and never rejected. Refusing the
 * seventh would be the first place the game says no, on the strength of a
 * count she can do herself. The clash marking already tells her what happened.
 *
 * It leaks nothing: `remaining` counts what is on the grid, which is in front
 * of her, and never what belongs there.
 */
function Choices({
  size,
  teddies,
  remaining,
  disabled,
  onChoose,
}: {
  size: SudokuSize
  teddies: readonly string[]
  remaining: readonly number[]
  /** The cursor is on a square the puzzle gave her, so there is nothing to do. */
  disabled: boolean
  onChoose: (value: number) => void
}) {
  const values = Array.from({ length: size }, (_, index) => index + 1)

  return (
    <ul
      aria-label="What to put in a square"
      className="flex flex-wrap items-center justify-center gap-2 [[data-scale=tv]_&]:gap-3"
    >
      {values.map((value) => {
        const key = teddies[value - 1]
        const meta = key ? TEDDIES[key as keyof typeof TEDDIES] : undefined
        const left = remaining[value - 1] ?? 0

        return (
          <li key={value}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChoose(value)}
              aria-label={`${meta ? meta.name : value}${left === 0 ? ', none left to place' : ''}`}
              className={[
                'border-border bg-surface-sunken flex items-center gap-1.5',
                // A finger needs a target; the television needs the height
                // back for the grid, and has a keyboard rather than a finger.
                'min-h-(--size-tap) [[data-scale=tv]_&]:min-h-0',
                'rounded-(--radius-control) border px-2 py-1 transition-opacity',
                'disabled:cursor-not-allowed disabled:opacity-40',
                // Spent, not forbidden. See the note above.
                left === 0 ? 'opacity-40' : '',
              ].join(' ')}
            >
              <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                <KeyCap>{value}</KeyCap>
              </span>

              {meta ? (
                <>
                  <img src={`/teddies/${meta.key}.webp`} alt="" className="size-8 object-contain" />
                  <span className="text-(length:--text-small)">{meta.name}</span>
                </>
              ) : (
                <span className="font-display text-(length:--text-body)" aria-hidden="true">
                  {value}
                </span>
              )}
            </button>
          </li>
        )
      })}
    </ul>
  )
}
